import { cn, GlassCard } from '@merritrac/shared/components';
import { MapPin, Users } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useTheme } from '../../lib/theme.js';

import type { ReactElement } from 'react';

export interface TechLocation {
  accessKey: number;
  user: string;
  lat: number;
  lng: number;
  address: string;
  isActive: boolean;
}

export interface JobLocation {
  accessKey: number;
  ticketno: string;
  client: string;
  lat: number;
  lng: number;
  status: 'Active' | 'Pending' | 'Overdue';
}

interface TechnicianMapProps {
  technicians: TechLocation[];
  jobs: JobLocation[];
  loading?: boolean;
  className?: string | undefined;
}

const TOKEN: string = (import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined) ?? '';

export function TechnicianMap({ technicians, jobs, className }: TechnicianMapProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<unknown>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (containerRef.current === null || mapInstanceRef.current !== null || TOKEN === '') {
      return;
    }

    let cancelled = false;

    void import('mapbox-gl').then((mapboxglModule) => {
      if (cancelled || containerRef.current === null) {
        return;
      }

      // Also load the CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
      document.head.appendChild(link);

      const mapboxgl = mapboxglModule.default;
      mapboxgl.accessToken = TOKEN;

      const map = new mapboxgl.Map({
        container: containerRef.current,
        style:
          theme === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
        center: [-79.3832, 43.6532],
        zoom: 10,
        attributionControl: false,
      });

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

      // Add markers once loaded
      map.on('load', () => {
        const bounds = new mapboxgl.LngLatBounds();
        let pointCount = 0;

        technicians.forEach((tech) => {
          if (tech.lat === 0 || tech.lng === 0) {
            return;
          }
          const el = document.createElement('div');
          el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${tech.isActive ? '#16a34a' : '#9ca3af'};border:2.5px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:pointer;`;
          new mapboxgl.Marker({ element: el })
            .setLngLat([tech.lng, tech.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 15, closeButton: false }).setText(
                `${tech.user} — ${tech.isActive ? 'Active' : 'Offline'}`,
              ),
            )
            .addTo(map);
          bounds.extend([tech.lng, tech.lat]);
          pointCount += 1;
        });

        jobs.forEach((job) => {
          if (job.lat === 0 || job.lng === 0) {
            return;
          }
          const colors = { Active: '#3b82f6', Pending: '#f59e0b', Overdue: '#ef4444' } as const;
          const c = colors[job.status];
          const el = document.createElement('div');
          el.style.cssText = `width:10px;height:10px;border-radius:50%;background:${c};border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);cursor:pointer;`;
          new mapboxgl.Marker({ element: el })
            .setLngLat([job.lng, job.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 12, closeButton: false }).setText(
                `${job.ticketno} — ${job.client}`,
              ),
            )
            .addTo(map);
          bounds.extend([job.lng, job.lat]);
          pointCount += 1;
        });

        if (pointCount > 0) {
          map.fitBounds(bounds, { padding: 50, maxZoom: 12 });
        }
      });

      mapInstanceRef.current = map;
    });

    return (): void => {
      cancelled = true;
      if (mapInstanceRef.current !== null) {
        (mapInstanceRef.current as { remove: () => void }).remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  if (TOKEN === '') {
    return (
      <GlassCard
        className={cn('flex items-center justify-center', className)}
        style={{ minHeight: '400px' }}
      >
        <div className="text-center">
          <MapPin size={24} className="text-primary mx-auto mb-2" />
          <p className="text-sm text-text-secondary">Set VITE_MAPBOX_TOKEN in .env.local</p>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className={cn('overflow-hidden relative', className)}>
      {/* Legend */}
      <div
        className="absolute top-3 left-3 z-10 rounded-lg px-3 py-2 text-xs space-y-1.5"
        style={{
          background: 'var(--glass-bg-strong, rgba(255,255,255,0.50))',
          backdropFilter: 'blur(8px)',
          border: '1px solid var(--glass-border-subtle, rgba(255,255,255,0.30))',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#16a34a' }} />
          <span className="text-text-secondary">Active Tech</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#3b82f6' }} />
          <span className="text-text-secondary">Active Job</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#f59e0b' }} />
          <span className="text-text-secondary">Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />
          <span className="text-text-secondary">Overdue</span>
        </div>
      </div>

      {/* Stats */}
      <div
        className="absolute bottom-3 left-3 z-10 rounded-lg px-3 py-2 text-xs flex gap-4"
        style={{
          background: 'var(--glass-bg-strong, rgba(255,255,255,0.50))',
          backdropFilter: 'blur(8px)',
          border: '1px solid var(--glass-border-subtle, rgba(255,255,255,0.30))',
        }}
      >
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-success" />
          <span className="text-text-secondary">
            {technicians.filter((t) => t.isActive).length} active
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin size={12} className="text-primary" />
          <span className="text-text-secondary">{jobs.length} jobs</span>
        </div>
      </div>

      {/* Map container — explicit height required for Mapbox */}
      <div ref={containerRef} style={{ width: '100%', height: '400px' }} />
    </GlassCard>
  );
}
